/**
 * Default-value builders for client / contact edit forms.
 */

import type { Companies } from "@/src/gql/graphql";

/**
 * Company relation fields read off a client/contact for edit defaults.
 *
 * Derived from the codegen `Companies` type rather than hand-rolled, so a
 * renamed or dropped column fails to compile here instead of quietly defaulting
 * the form field to "". `Partial<>` keeps callers that carry a subset legal
 * (ENTITY_DETAIL_QUERY_FIELD_COVERAGE — nested relation coverage).
 */
type ClientCompanyRelation = Partial<
  Pick<
    Companies,
    | "website"
    | "industry"
    | "contract_type"
    | "hourly_rate"
    | "monthly_rate"
    | "payment_terms"
    | "tax_id"
  >
>;

/** Structural shape of a client/contact for edit-form defaults. */
interface EditableClient {
  name?: string | null;
  company_name?: string | null;
  email?: string | null;
  phone?: string | null;
  address?: string | null;
  city?: string | null;
  state?: string | null;
  zip_code?: string | null;
  country?: string | null;
  notes?: string | null;
  is_active?: boolean | null;
  companies?: ClientCompanyRelation | null;
}

/**
 * Convert a Client/Contact to EditClientForm default values
 * Maps client data and company relations to form fields
 */
export function clientToEditFormDefaults(client: EditableClient) {
  return {
    // Contact Information
    name: client.name || "",
    company_name: client.company_name || "",
    email: client.email || "",
    phone: client.phone || "",
    website: client.companies?.website || "",
    industry: client.companies?.industry || "",

    // Address Information
    address: client.address || "",
    city: client.city || "",
    state: client.state || "",
    zip_code: client.zip_code || "",
    country: client.country || "",

    // Contract & Financial
    contract_type: client.companies?.contract_type || undefined,
    hourly_rate: client.companies?.hourly_rate || undefined,
    monthly_rate: client.companies?.monthly_rate || undefined,
    payment_terms: client.companies?.payment_terms || "",
    tax_id: client.companies?.tax_id || "",

    // Notes
    notes: client.notes || "",

    // Status
    is_active: client.is_active ?? true,
  };
}
